import { useEffect, useState } from 'react';
import { authApi, type SessionUser } from './api';

/**
 * Who is logged in, resolved once on mount.
 *
 * `/api/me` answers 200 with `user: null` for an anonymous visitor rather than
 * 401, so being logged out is an ordinary state here and not an error path —
 * the quote tool stays fully usable without a session, and logging in only adds
 * features on top.
 *
 * `loading` matters: it distinguishes "not logged in" from "we do not know
 * yet", which is what stops the rep-name field from being pre-filled a moment
 * after the user has already started typing in it.
 */
export interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => void;
}

export function useSession(): SessionState {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authApi
      .me()
      .then((r) => { if (!cancelled) setUser(r.user); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tick]);

  return { user, loading, refresh: () => setTick((t) => t + 1) };
}
